<script setup lang="ts">
import { isBankAccountDepositMethod as isBankAccountConsentRequired } from './bank-account-consent';
import type DocumentCpf from '@/components/deposit/DocumentCpf.vue';
import type { UserDocument } from '@/types/UserData';

type Props = {
  userCurrency: string;
  userCountry: string;
  userHasDocument: boolean;
  minDepositAmount: number;
  maxDepositAmount: number;
  loading?: boolean;
  initialAmount?: string;
  // Erro de limite do core: já vem como HTML (link pros limites), por isso é
  // renderizado com v-html e fica parado na tela, sem virar toast.
  limitAlertError?: string;
  activeFirstDepositMessage?: boolean;
  firstDepositMessage?: string;
};

const {
  userCurrency,
  userCountry,
  userHasDocument,
  minDepositAmount,
  maxDepositAmount,
  loading = false,
  initialAmount,
  limitAlertError = '',
  activeFirstDepositMessage = false,
  firstDepositMessage,
} = defineProps<Props>();

export type GameDepositSubmitPayload = {
  // valor em centavos
  amount: number;
  doc: {
    docnumber: string;
    docnumber_valid: boolean;
    captcha_token: string;
  };
  // Consentimento de cadastro da conta bancária (`save_bank_account` na API).
  saveBankAccount: boolean;
};

type Emits = {
  submit: [GameDepositSubmitPayload];
};

const emit = defineEmits<Emits>();

const { showMoneyFormat, walletHasFirstDeposit } = useWallet();

const { close: closeDepositModal } = useGameDepositModal();

// Leva pros dados da conta, onde o país/moeda é editável (o fluxo antigo manda
// pro mesmo lugar com o mesmo destaque).
function goToChangeCountry() {
  closeDepositModal();
  navigateTo({
    path: localeLink('/user/account'),
    query: { highlight: 'infos' },
  });
}

// Seletor de forma de pagamento: provedores ativos vindos da rota (/payments),
// igual ao fluxo principal. Em fallback vem só 1 (PIX); sem fallback, vários.
const {
  numberOfDepositMethods,
  allDepositMethods,
  defaultSelectedDepositMethod,
} = usePayments();

const { isLoading: isPaymentsLoading } = storeToRefs(usePaymentsStore());
const { currentDepositMethod } = storeToRefs(useWalletStore());
const { selectDepositMethod } = useWalletStore();

const hasAnyDepositMethod = computed(() => numberOfDepositMethods.value >= 1);

// Ao montar, garante um método válido — mas SÓ se o atual não estiver na
// lista. Este componente remonta ao voltar da Etapa 2 ("voltar", "tentar de
// novo"); resetar sempre pro default jogava fora o provedor que o jogador
// escolheu (cartão virava PIX).
onMounted(() => {
  const isValid = allDepositMethods.value.some(
    (m) => m.slug === currentDepositMethod.value,
  );

  if (!isValid) selectDepositMethod(defaultSelectedDepositMethod.value);
});

// O store inicia em 'pix' e, sem fallback, o fetch não seta o default (vide
// fetchPaymentsFromApiNew). Quando a lista de provedores chega/atualiza, se o
// método atual não existir nela cai no default — cobre a corrida do fetch async
// (que o fluxo principal não trata).
watch(allDepositMethods, (methods) => {
  const isValid = methods.some((m) => m.slug === currentDepositMethod.value);

  if (!isValid) selectDepositMethod(defaultSelectedDepositMethod.value);
});

// --- Consentimento de cadastro da conta bancária ----------------------------
// Campo `save_bank_account`, obrigatório na API (/wallet/add-credit): sem ele a
// requisição volta 422. Quem autoriza é o jogador, então é checkbox — mesma
// regra e mesma copy do fluxo principal (components/deposit/DefaultLayout.vue).
const { hasBankAccount } = useUserData();

const isBankAccountDepositMethod = computed(() =>
  isBankAccountConsentRequired(currentDepositMethod.value),
);

const saveBankAccount = ref(false);

// Obrigatório, mas não desabilita o PROSSEGUIR: clicar sem marcar mostra o erro
// em vez de deixar o botão morto sem explicação. Limpa ao marcar.
const showBankAccountConsentError = ref(false);

// `:key` da linha do checkbox: incrementar destrói/recria o elemento, senão a
// @keyframes de chacoalhar só toca na primeira vez (reaplicar a mesma classe
// não reinicia a animação de forma confiável).
const checkboxShakeKey = ref(0);

watch(saveBankAccount, (checked) => {
  if (checked) showBankAccountConsentError.value = false;
});

// Trocar de provedor esconde/reexibe a caixa; sem isso o erro de uma tentativa
// anterior reaparecia vermelho de cara ao voltar pra um método bancário.
watch(currentDepositMethod, () => {
  showBankAccountConsentError.value = false;
});

// Valor em centavos (máscara que sempre exibe "X,XX"). Padrão R$ 250,00.
// Pode ser sobrescrito por initialAmount (deeplink / window.openPixDeposit).
const DEFAULT_CENTS = 25000;
const STEP_CENTS = 1000; // R$ 10,00

function reaisToCents(reais: string | number): number {
  const n = parseFloat(String(reais).replace(',', '.'));

  return isNaN(n) ? 0 : Math.round(n * 100);
}

const amountCents = ref<number>(
  initialAmount ? reaisToCents(initialAmount) : DEFAULT_CENTS,
);

// Exibição com vírgula e 2 casas: "250,00", "1.234,56".
const displayValue = computed(() =>
  (amountCents.value / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
);

// Máscara de centavos: aceita só dígitos, preenchendo da direita. Crava no
// máximo permitido — digitar acima do maxDepositAmount trava no teto.
function onAmountInput(event: Event) {
  const el = event.target as HTMLInputElement;
  const digits = el.value.replace(/\D/g, '').slice(0, 12);

  amountCents.value = Math.min(Number(digits || '0'), maxDepositAmount);

  // Força o DOM a refletir o valor formatado. Sem isso, digitar uma letra
  // (que não altera amountCents) deixaria o caractere visível no input,
  // pois o Vue não re-renderiza quando o :value computado não muda.
  el.value = displayValue.value;
}

const fullValueDepositAmount = computed(() => amountCents.value);

const amountInteger = computed(() => amountCents.value / 100);

function stepAmount(delta: number) {
  amountCents.value = Math.min(
    maxDepositAmount,
    Math.max(0, amountCents.value + delta),
  );
}

// --- Chips de valor rápido (conforme Figma) ---------------------------------
const chipValues = computed<number[]>(() => {
  if (['NGN', 'ARS', 'UYU'].includes(userCurrency)) {
    return [1000, 2000, 4000, 10000, 20000];
  }
  if (userCurrency === 'MXN') return [200, 500, 1000, 2500, 5000];
  if (userCurrency === 'INR') return [100, 200, 400, 1000, 2000];

  return [20, 50, 100, 250, 500, 1000, 2500, 5000];
});

function canShowChip(value: number): boolean {
  if (value * 100 < minDepositAmount) return false;
  if (value * 100 > maxDepositAmount) return false;

  return true;
}

const visibleChips = computed(() => chipValues.value.filter(canShowChip));

function selectChip(value: number) {
  amountCents.value = value * 100;
}

// Drag-to-scroll dos chips com mouse (desktop), espelhando o BaseFilterTabs.
// No toque, mantém o scroll nativo.
const DRAG_THRESHOLD_PX = 5;
const chipsRef = ref<HTMLElement | null>(null);
const isDraggingChips = ref(false);

let chipStartX = 0;
let chipStartScrollLeft = 0;
let chipMoved = false;
let chipPointerId: number | null = null;

function onChipsPointerDown(e: PointerEvent) {
  if (e.pointerType !== 'mouse' || !chipsRef.value) return;

  isDraggingChips.value = true;
  chipMoved = false;
  chipStartX = e.clientX;
  chipStartScrollLeft = chipsRef.value.scrollLeft;
  chipPointerId = e.pointerId;
}

function onChipsPointerMove(e: PointerEvent) {
  if (!isDraggingChips.value || !chipsRef.value) return;

  const dx = e.clientX - chipStartX;

  if (Math.abs(dx) > DRAG_THRESHOLD_PX && !chipMoved) {
    chipMoved = true;
    // Só captura o ponteiro depois que o drag começa de fato: o drag continua
    // mesmo com o mouse fora da faixa de chips (sem isso o pointerleave matava
    // o scroll no meio do swipe). Capturar no pointerdown quebrava o clique,
    // pois o `click` passava a cair no container e não no botão do chip.
    chipsRef.value.setPointerCapture(e.pointerId);
  }

  chipsRef.value.scrollLeft = chipStartScrollLeft - dx;
}

function endChipsDrag() {
  if (chipsRef.value && chipPointerId !== null) {
    if (chipsRef.value.hasPointerCapture(chipPointerId)) {
      chipsRef.value.releasePointerCapture(chipPointerId);
    }
    chipPointerId = null;
  }
  isDraggingChips.value = false;
}

// Bloqueia o clique-fantasma ao soltar um arrasto (sem afetar clique limpo).
function onChipsClickCapture(e: MouseEvent) {
  if (chipMoved) {
    e.stopPropagation();
    e.preventDefault();
    chipMoved = false;
  }
}

// --- Documento (CPF) para BRA sem documento, espelhando o DefaultLayout ---
const doc = ref({
  docnumber: '',
  docnumber_valid: false,
});

const documentCpfRef = ref<InstanceType<typeof DocumentCpf> | null>(null);
const antiRobotToken = computed(
  () => documentCpfRef.value?.antiRobotToken ?? '',
);

function updateDocumentFromComponent(data: UserDocument) {
  doc.value.docnumber_valid = data.docnumber_valid || false;
  doc.value.docnumber = data.docnumber || '';
}

const isDocumentValid = computed<boolean>(() => {
  if (userHasDocument) return true;
  if (userCountry === 'MEX') return !!doc.value.docnumber;
  if (userCountry === 'BRA') {
    return !!doc.value.docnumber && !!doc.value.docnumber_valid;
  }

  return true;
});

const isSubmitEnabled = computed<boolean>(() => {
  if (loading) return false;
  // Sem provedor ativo pra moeda/país do jogador não há o que enviar: o
  // `currentDepositMethod` seria só o default do store ('pix'), e o depósito
  // iria pro core com um método que não está na lista.
  if (!hasAnyDepositMethod.value) return false;
  if (!isDocumentValid.value) return false;

  if (fullValueDepositAmount.value > maxDepositAmount) return false;

  return fullValueDepositAmount.value >= minDepositAmount;
});

const isAmountDirty = ref(false);

watch(amountCents, () => {
  isAmountDirty.value = true;
});

// Alerta vermelho de limite (Figma): aparece ao cravar no máximo (o valor já
// é limitado ao teto) ou ao ficar abaixo do mínimo. Label normal + valor bold.
const amountAlert = computed<{ label: string; value: string } | null>(() => {
  if (amountCents.value >= maxDepositAmount) {
    return {
      label: 'Depósito máximo até',
      value: showMoneyFormat({
        value: maxDepositAmount / 100,
        withCents: true,
      }),
    };
  }
  if (isAmountDirty.value && amountCents.value < minDepositAmount) {
    return {
      label: 'Depósito mínimo de',
      value: showMoneyFormat({
        value: minDepositAmount / 100,
        withCents: true,
      }),
    };
  }

  return null;
});

function onSubmit() {
  if (!isSubmitEnabled.value) return;

  if (isBankAccountDepositMethod.value && !saveBankAccount.value) {
    showBankAccountConsentError.value = true;
    checkboxShakeKey.value++;

    return;
  }

  emit('submit', {
    amount: fullValueDepositAmount.value,
    doc: {
      docnumber: doc.value.docnumber.replaceAll('-', '').replaceAll('.', ''),
      docnumber_valid: doc.value.docnumber_valid,
      captcha_token: antiRobotToken.value,
    },
    saveBankAccount: saveBankAccount.value,
  });
}
</script>

<template>
  <form
    class="gdm-amount"
    @submit.prevent="onSubmit"
  >
    <!-- Forma de pagamento: provedores ativos da rota (/payments). Em fallback
         vem só 1 (PIX); sem fallback, vários — mesma lógica/condição do fluxo
         principal (DepositMethodSelector). -->
    <div
      v-if="hasAnyDepositMethod && !isPaymentsLoading"
      class="gdm-methods"
    >
      <DepositMethodSelector
        :selected-deposit-method="currentDepositMethod"
        @select-deposit-method="selectDepositMethod"
      />
    </div>

    <!-- Nenhum provedor ativo pra moeda/país do jogador. A lista muda em
         runtime (/payments), então este estado é alcançável sem deploy — o
         fluxo principal avisa (components/deposit/index.vue) e aqui também.
         O PROSSEGUIR fica travado junto (isSubmitEnabled). -->
    <div
      v-else-if="!isPaymentsLoading"
      class="gdm-no-method"
      role="alert"
    >
      {{
        $t('deposit.general.no_pay_method', {
          currency: userCurrency,
          country: userCountry,
        })
      }}
    </div>

    <!-- Quem ainda não depositou pode trocar país/moeda e passar a ter método
         disponível — mesma saída do fluxo antigo. -->
    <button
      v-if="
        !hasAnyDepositMethod && !isPaymentsLoading && !walletHasFirstDeposit
      "
      class="gdm-change-country"
      type="button"
      @click="goToChangeCountry"
    >
      {{ $t('deposit.general.can_change_country') }}
    </button>

    <!-- Label do valor -->
    <div class="gdm-amount-head">
      <span class="gdm-amount-label">Valor a ser depositado</span>
    </div>

    <!-- Campo de valor com botões − / + -->
    <div class="gdm-value-row">
      <button
        aria-label="Diminuir"
        class="gdm-step-btn"
        type="button"
        @click="stepAmount(-STEP_CENTS)"
      >
        <svg
          fill="none"
          height="2"
          viewBox="0 0 14 2"
          width="14"
        >
          <path
            d="M1 1H13"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-width="2"
          />
        </svg>
      </button>

      <div class="gdm-value-field">
        <span class="gdm-value-currency">R$</span>
        <input
          id="gdmInputCurrency"
          class="gdm-value-input"
          inputmode="numeric"
          :size="displayValue.length || 1"
          :value="displayValue"
          @input="onAmountInput"
        />
      </div>

      <button
        aria-label="Aumentar"
        class="gdm-step-btn"
        type="button"
        @click="stepAmount(STEP_CENTS)"
      >
        <svg
          fill="none"
          height="14"
          viewBox="0 0 14 14"
          width="14"
        >
          <path
            d="M7 1V13M1 7H13"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-width="2"
          />
        </svg>
      </button>
    </div>

    <!-- Chips de valor rápido (HOT no topo) -->
    <div
      ref="chipsRef"
      class="gdm-chips"
      :class="{ 'is-dragging': isDraggingChips }"
      @click.capture="onChipsClickCapture"
      @pointerdown="onChipsPointerDown"
      @pointerleave="endChipsDrag"
      @pointermove="onChipsPointerMove"
      @pointerup="endChipsDrag"
    >
      <button
        v-for="value in visibleChips"
        :key="value"
        class="gdm-chip"
        :class="{ 'is-current': amountInteger === value }"
        type="button"
        @click="selectChip(value)"
      >
        {{ showMoneyFormat({ value, withCents: false }) }}
      </button>
    </div>

    <!-- Alerta de limite (máximo/mínimo) — estilo Figma -->
    <div
      v-if="amountAlert"
      class="gdm-amount-alert"
    >
      {{ amountAlert.label }}
      <strong>{{ amountAlert.value }}</strong>
    </div>

    <!-- Documento CPF/CURP quando o usuário ainda não tem documento -->
    <DepositDocumentCurp
      v-if="userCountry === 'MEX' && !userHasDocument"
      v-model="doc.docnumber"
      :user-has-document="userHasDocument"
    />
    <DepositDocumentCpf
      v-else-if="userCountry === 'BRA' && !userHasDocument"
      ref="documentCpfRef"
      :user-has-document="userHasDocument"
      @update-user-document-cpf="updateDocumentFromComponent"
    />

    <!-- Consentimento de cadastro da conta bancária: `save_bank_account`, campo
         obrigatório da API. Mesma copy do fluxo principal (as duas variantes
         dependem de já haver conta cadastrada). -->
    <label
      v-if="isBankAccountDepositMethod"
      :key="checkboxShakeKey"
      class="gdm-consent"
      :class="{ 'is-error': showBankAccountConsentError }"
      for="gdmSaveBankAccount"
    >
      <span class="gdm-consent-box-wrap">
        <input
          id="gdmSaveBankAccount"
          v-model="saveBankAccount"
          :aria-invalid="showBankAccountConsentError"
          class="gdm-consent-input"
          type="checkbox"
        />
        <span
          aria-hidden="true"
          class="gdm-consent-box"
          :class="{ 'is-checked': saveBankAccount }"
        >
          <svg
            v-if="saveBankAccount"
            fill="none"
            height="12"
            viewBox="0 0 16 16"
            width="12"
          >
            <path
              d="M3 8.5l3.2 3.2L13 4.8"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
        </span>
      </span>
      <span class="gdm-consent-text">
        {{
          hasBankAccount
            ? $t('deposit.general.bank_account_consent_different_account')
            : $t('deposit.general.bank_account_consent')
        }}
        <span
          v-if="showBankAccountConsentError"
          :aria-label="$t('deposit.general.bank_account_consent_required')"
          class="gdm-consent-asterisk"
          role="alert"
        >
          *
        </span>
      </span>
    </label>

    <!-- Mensagem de primeiro depósito (backoffice). Vem com HTML do CMS. -->
    <div
      v-if="activeFirstDepositMessage && firstDepositMessage"
      class="gdm-first-deposit"
      v-html="firstDepositMessage"
    />

    <!-- Erro de limite: inline e persistente, nunca toaster. -->
    <div
      v-if="limitAlertError"
      class="gdm-limit-alert"
      role="alert"
      v-html="limitAlertError"
    />

    <button
      class="gdm-submit"
      :disabled="!isSubmitEnabled"
      type="submit"
    >
      PROSSEGUIR
    </button>

    <!-- Compliance oficial (ENG-404): advertência + 18+ + identificação,
         mesma fonte de verdade do modal de depósito antigo -->
    <BaseComplianceInfo />
  </form>
</template>

<style scoped lang="scss">
$accent: #0485f7;
$orange: #ff3700;
$border: #28282c;
$chip-bg: rgba(39, 47, 63, 0.4);

.gdm-amount {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gdm-methods {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.gdm-amount-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .gdm-amount-label {
    font-size: 12px;
    font-weight: 400;
    color: #fcfcfc;
  }
}

.gdm-value-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  .gdm-step-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 16px;
    border: 1px solid $border;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  // Campo do valor: prefixo "R$" + input. Largura = conteúdo (sem flex:1); o
  // space-between do row + botões −/+ iguais (32px) deixam o grupo centralizado.
  .gdm-value-field {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .gdm-value-currency {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 36px;
    font-weight: 800;
    line-height: 48px;
    color: #fff;
  }

  .gdm-value-input {
    background: transparent;
    border: none;
    outline: none;
    text-align: center;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 36px;
    font-weight: 800;
    // line-height/height generosos pra conter o descendente da vírgula sem
    // gerar overflow vertical (que causava o scroll/tique no input).
    line-height: 48px;
    color: #fff;
    padding: 0;
    height: 48px;
    // Largura = conteúdo: o atributo `size` (= nº de caracteres) garante o
    // ajuste em todos os browsers; field-sizing deixa exato nos modernos.
    // flex: none impede o input de encolher (que causava scroll interno).
    flex: none;
    width: auto;
    field-sizing: content;
    // Clipa qualquer scroll interno do input (vertical/horizontal "ticando").
    overflow: hidden;
  }
}

.gdm-chips {
  display: flex;
  gap: 8px;
  width: 100%;
  overflow-x: auto;
  // Padding interno pra que o scroll horizontal não recorte o anel azul do
  // chip selecionado (box-shadow).
  padding: 4px 3px;
  scrollbar-width: none;
  // Drag-to-scroll com mouse no desktop (vide handlers de pointer).
  cursor: grab;

  &.is-dragging {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  .gdm-chip {
    // Largura acompanha o conteúdo (valores grandes como "R$ 5.000" cabem sem
    // espremer). Não estica nem encolhe; respira pelo padding lateral em px.
    flex: 0 0 auto;
    padding: 0 16px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 13px;
    background: $chip-bg;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition:
      background 0.15s ease,
      box-shadow 0.15s ease;

    &.is-current {
      box-shadow: 0 0 0 1px $accent;
    }
  }
}

// Consentimento de cadastro da conta bancária (`save_bank_account`). Paleta do
// modal, comportamento do fluxo principal: vira vermelho e chacoalha quando o
// jogador tenta prosseguir sem marcar.
.gdm-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(4, 133, 247, 0.2);
  border-radius: 12px;
  background: rgba(4, 133, 247, 0.06);
  cursor: pointer;

  // O reset global de `transition` (main.scss) mata transição no app inteiro —
  // por isso o feedback de erro é @keyframes, não transition.
  &.is-error {
    border-color: rgba(239, 68, 68, 0.4);
    background: rgba(255, 56, 60, 0.06);
    animation: gdm-consent-shake 0.4s ease-in-out;
  }
}

.gdm-consent-box-wrap {
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
}

.gdm-consent-input {
  position: absolute;
  inset: 0;
  width: 20px;
  height: 20px;
  opacity: 0;
  cursor: pointer;
}

.gdm-consent-box {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(255, 255, 255, 0.4);
  border-radius: 5px;
  color: #fff;

  &.is-checked {
    border-color: $accent;
    background: $accent;
  }

  .gdm-consent.is-error & {
    border-color: #ef4444;
  }
}

.gdm-consent-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 1.5;
  text-align: left;

  .gdm-consent.is-error & {
    color: #ef4444;
  }
}

.gdm-consent-asterisk {
  color: #ef4444;
  font-weight: 700;
}

@keyframes gdm-consent-shake {
  10%,
  90% {
    transform: translate(-1px, 0.5px);
  }

  20%,
  80% {
    transform: translate(2px, -0.5px);
  }

  30%,
  50%,
  70% {
    transform: translate(-4px, 0.3px);
  }

  40%,
  60% {
    transform: translate(4px, -0.3px);
  }
}

// Sem provedor disponível (lista de runtime vazia pra moeda/país do jogador).
// Mensagem de primeiro depósito, vinda do backoffice (HTML do CMS).
.gdm-first-deposit {
  width: 100%;
  padding: 10px 11px;
  border-radius: 12px;
  background: rgba(4, 133, 247, 0.1);
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 16px;
  text-align: center;

  :deep(a) {
    color: $accent;
    text-decoration: underline;
  }
}

// Erro de limite: vermelho, parado na tela até a próxima tentativa.
.gdm-limit-alert {
  width: 100%;
  padding: 12px 11px;
  border-radius: 12px;
  background: rgba(255, 56, 60, 0.15);
  color: #ef4444;
  font-size: 12px;
  line-height: 16px;
  text-align: center;

  :deep(a),
  :deep(strong) {
    color: #fff;
    font-weight: 700;
  }

  :deep(a) {
    text-decoration: underline;
  }
}

.gdm-change-country {
  width: 100%;
  padding: 12px 11px;
  border: 1px solid rgba(62, 255, 110, 0.3);
  border-radius: 12px;
  background: rgba(62, 255, 110, 0.08);
  color: #3eff6e;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  cursor: pointer;
}

.gdm-no-method {
  width: 100%;
  padding: 12px 11px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}

.gdm-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  margin-top: 4px;
  border-radius: 12px;
  background: $orange;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.05);
  }

  &[disabled]:not([disabled='false']) {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Alerta vermelho de limite (Figma node 19831:57099): danger-soft + texto red.
.gdm-amount-alert {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 12px 11px;
  border-radius: 12px;
  background: rgba(255, 56, 60, 0.15);
  color: #ef4444;
  font-size: 12px;
  line-height: 16px;
  text-align: center;

  strong {
    font-weight: 700;
  }
}
</style>
